import { useState } from "react";

interface SendScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSendNow: () => void;
  onSchedule: (scheduleDate: string) => void;
  onGoToKanban?: () => void;
  onGoToKanbanAndMoveToContacted?: () => void;
  showPostSendActions?: boolean;
  initialChoice?: "send" | "schedule";
  initialScheduleDate?: string;
}

function toLocalDateTimeValue(date: Date): string {
  // Returns YYYY-MM-DD for input type="date"
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function SendScheduleModal({
  isOpen,
  onClose,
  onSendNow,
  onSchedule,
  onGoToKanban,
  onGoToKanbanAndMoveToContacted,
  showPostSendActions = false,
  initialChoice = new Date().getHours() < 18 ? "send" : "schedule",
  initialScheduleDate = toLocalDateTimeValue(new Date(Date.now() + 24 * 60 * 60 * 1000))
}: SendScheduleModalProps) {
  const [sendChoice, setSendChoice] = useState<"send" | "schedule">(initialChoice);
  const [scheduleAt, setScheduleAt] = useState<string>(initialScheduleDate);

  if (!isOpen) return null;

  const handleSendNow = () => {
    onSendNow();
  };

  const handleSchedule = () => {
    if (!scheduleAt) return;
    onSchedule(scheduleAt);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center font-sans">
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <div className="relative bg-surface w-full max-w-md mx-4 rounded-2xl shadow-float border border-border">
        {!showPostSendActions ? (
          <>
            <div className="px-5 py-4 border-b border-border">
              <div className="text-xs font-medium text-ink">Send or schedule</div>
              <div className="text-xs text-ink-muted mt-1">Choose how you'd like to proceed</div>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  id="choice-send"
                  type="radio"
                  name="sendChoice"
                  checked={sendChoice === "send"}
                  onChange={() => setSendChoice("send")}
                  className="accent-accent"
                />
                <label htmlFor="choice-send" className="text-xs text-ink-muted">Send now</label>
              </div>
              <div className="flex items-start gap-2">
                <input
                  id="choice-schedule"
                  type="radio"
                  name="sendChoice"
                  checked={sendChoice === "schedule"}
                  onChange={() => setSendChoice("schedule")}
                  className="accent-accent"
                />
                <div className="flex-1">
                  <label htmlFor="scheduleAt" className="text-xs text-ink-muted">Schedule</label>
                  {sendChoice === "schedule" && (
                    <div className="mt-2">
                      <input
                        id="scheduleAt"
                        type="date"
                        className="w-full border border-border rounded-lg px-2 py-1 text-xs bg-surface text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        value={scheduleAt}
                        onChange={(e) => setScheduleAt(e.target.value)}
                      />
                      <div className="text-xs text-ink-muted mt-1">This will be recorded in logs as a scheduled email.</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
              <button
                className="px-3 h-8 text-xs border border-border rounded-lg bg-surface text-ink-muted hover:bg-surface-sunken transition-colors duration-150"
                onClick={onClose}
              >
                Cancel
              </button>
              {sendChoice === "send" ? (
                <button
                  className="px-3 h-8 text-xs rounded-lg bg-ink text-ink-inverse hover:bg-ink/90 transition-colors duration-150"
                  onClick={handleSendNow}
                >
                  Send now & log
                </button>
              ) : (
                <button
                  className="px-3 h-8 text-xs rounded-lg bg-ink text-ink-inverse hover:bg-ink/90 transition-colors duration-150 disabled:opacity-50"
                  onClick={handleSchedule}
                  disabled={!scheduleAt}
                >
                  Schedule & log
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="px-5 py-4 border-b border-border">
              <div className="text-sm font-medium text-ink">Email sent successfully</div>
              <div className="text-xs text-ink-muted mt-1">What would you like to do next ?</div>
            </div>
            <div className="px-5 py-4 space-y-3">
              <button
                className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-surface text-ink-muted hover:bg-surface-sunken transition-colors duration-150 text-left"
                onClick={onGoToKanban}
              >
                Go back to kanban
              </button>
              <button
                className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-surface text-ink-muted hover:bg-surface-sunken transition-colors duration-150 text-left"
                onClick={onGoToKanbanAndMoveToContacted}
              >
                Go back to kanban and move to contacted
              </button>
            </div>
            <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
              <button
                className="px-3 h-8 text-xs border border-border rounded-lg bg-surface text-ink-muted hover:bg-surface-sunken transition-colors duration-150"
                onClick={onClose}
              >
                Stay here
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}